/* Root & Harmony motion engine — reveal / word stagger / steps / review scroller / popup.
   No dependencies. Transform + opacity only. Honours prefers-reduced-motion.
   Re-initialises on shopify:section:load so the theme editor stays live. */
(function () {
  'use strict';
  document.documentElement.classList.add('js');
  var RM = window.matchMedia('(prefers-reduced-motion: reduce)');

  function each(root, sel, fn) {
    Array.prototype.forEach.call((root || document).querySelectorAll(sel), fn);
  }

  /* ---- reveal (descendant query, never :scope > *) ---------------------- */
  var revealed = [];
  function countUp(el) {
    if (el.dataset.rhCounted) return;
    el.dataset.rhCounted = '1';
    var to = parseFloat(el.getAttribute('data-to')), suffix = el.getAttribute('data-suffix') || '', dur = 1300, t0 = null;
    if (isNaN(to) || RM.matches) { el.textContent = el.getAttribute('data-final'); return; }
    var dec = (String(to).split('.')[1] || '').length;
    function step(ts) {
      if (!t0) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur), e = 1 - Math.pow(1 - p, 3);
      el.textContent = (to * e).toFixed(dec) + suffix;
      if (p < 1) requestAnimationFrame(step); else el.textContent = el.getAttribute('data-final');
    }
    requestAnimationFrame(step);
  }
  function markIn(n) {
    n.classList.add('is-in');
    each(n, '[data-rh-count]', countUp);
    var kids = n.querySelectorAll('.rh-r');
    Array.prototype.forEach.call(kids, function (c, i) {
      if (!c.style.getPropertyValue('--i')) c.style.setProperty('--i', i);
      c.classList.add('is-in');
    });
    if (n.classList.contains('rh-r')) n.classList.add('is-in');
  }
  function reveal(root) {
    var nodes = (root || document).querySelectorAll('[data-rh-reveal]');
    if (!nodes.length) return;
    if (RM.matches || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(nodes, markIn);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        // reveal on intersection OR when the element has already passed above the viewport
        // (a fast flick can cross the viewport between two IO deliveries)
        if (!e.isIntersecting && e.boundingClientRect.top >= 0) return;
        markIn(e.target);
        io.unobserve(e.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    Array.prototype.forEach.call(nodes, function (n) {
      if (revealed.indexOf(n) !== -1) return;
      revealed.push(n);
      io.observe(n);
    });
  }
  // rAF-throttled scroll sweep: anything above the fold that IO never reported
  var sweeping = false;
  function sweep() {
    if (sweeping) return;
    sweeping = true;
    requestAnimationFrame(function () {
      var vh = window.innerHeight;
      for (var i = 0; i < revealed.length; i++) {
        var n = revealed[i];
        if (n.classList.contains('is-in')) continue;
        var r = n.getBoundingClientRect();
        if (r.top < vh * 0.92 && r.bottom > 0 || r.bottom < 0) markIn(n);
      }
      sweeping = false;
    });
  }

  /* ---- word stagger on ONE heading (the hero H1) ------------------------ */
  function words(root) {
    each(root, '[data-rh-words]', function (h) {
      if (h.dataset.rhWordsDone || RM.matches) return;
      h.dataset.rhWordsDone = '1';
      // only split plain text nodes; keep the highlight span intact as one word
      var frag = document.createDocumentFragment(), idx = 0;
      Array.prototype.slice.call(h.childNodes).forEach(function (node) {
        if (node.nodeType === 3) {
          node.textContent.split(/(\s+)/).forEach(function (t) {
            if (!t) return;
            if (/^\s+$/.test(t)) { frag.appendChild(document.createTextNode(t)); return; }
            var s = document.createElement('span');
            s.className = 'rh-hero__word rh-r';
            s.style.setProperty('--i', idx++);
            s.setAttribute('aria-hidden', 'true');
            s.textContent = t;
            frag.appendChild(s);
          });
        } else if (node.nodeType === 1 && node.tagName === 'BR') {
          frag.appendChild(node.cloneNode());
        } else if (node.nodeType === 1) {
          node.classList.add('rh-r', 'rh-hero__word');
          node.style.setProperty('--i', idx++);
          node.setAttribute('aria-hidden', 'true');
          frag.appendChild(node);
        }
      });
      h.setAttribute('aria-label', h.textContent.replace(/\s+/g, ' ').trim());
      while (h.firstChild) h.removeChild(h.firstChild);
      h.appendChild(frag);
      h.classList.remove('rh-r');
    });
  }

  /* ---- steps: exactly one active ---------------------------------------- */
  function steps(root) {
    each(root, '[data-rh-steps]', function (sec) {
      if (sec.dataset.rhStepsDone) return;
      sec.dataset.rhStepsDone = '1';
      var items = sec.querySelectorAll('[data-rh-step]');
      if (!items.length) return;
      function setActive(i) {
        for (var k = 0; k < items.length; k++) items[k].classList.toggle('is-active', k === i);
      }
      setActive(0);
      if (!('IntersectionObserver' in window) || RM.matches) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting && e.intersectionRatio > 0.5) setActive(parseInt(e.target.getAttribute('data-rh-step'), 10));
        });
      }, { threshold: [0.5, 0.75], rootMargin: '-30% 0px -30% 0px' });
      Array.prototype.forEach.call(items, function (s) { io.observe(s); });
    });
  }

  /* ---- review scroller arrows ------------------------------------------ */
  function scroller(root) {
    each(root, '[data-rh-scroller]', function (sec) {
      if (sec.dataset.rhScrollDone) return;
      sec.dataset.rhScrollDone = '1';
      var row = sec.querySelector('[data-rh-row]');
      if (!row) return;
      each(sec, '[data-rh-prev],[data-rh-next]', function (b) {
        b.addEventListener('click', function () {
          var card = row.firstElementChild;
          var step = card ? card.getBoundingClientRect().width + 20 : 320;
          row.scrollBy({ left: b.hasAttribute('data-rh-next') ? step : -step, behavior: RM.matches ? 'auto' : 'smooth' });
        });
      });
    });
  }

  /* ---- email popup ------------------------------------------------------ */
  function popup(root) {
    each(root, '[data-rh-popup]', function (p) {
      if (p.dataset.rhPopupDone) return;
      p.dataset.rhPopupDone = '1';
      var key = 'rh-popup-seen';
      var delay = parseInt(p.getAttribute('data-delay'), 10) || 6;
      var days = parseInt(p.getAttribute('data-days'), 10) || 7;
      var enabled = p.getAttribute('data-enabled') === 'true';
      var design = !!window.Shopify && !!window.Shopify.designMode;
      var closeBtns = p.querySelectorAll('[data-rh-popup-close]');
      var timer;
      function open() { p.classList.add('is-open'); document.body.style.overflow = 'hidden'; var f = p.querySelector('input[type="email"]'); if (f) setTimeout(function () { f.focus(); }, 60); }
      function close() {
        p.classList.remove('is-open'); document.body.style.overflow = '';
        try { localStorage.setItem(key, String(Date.now())); } catch (e) {}
      }
      Array.prototype.forEach.call(closeBtns, function (b) { b.addEventListener('click', close); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && p.classList.contains('is-open')) close(); });
      if (design) {
        document.addEventListener('shopify:section:select', function (e) { if (e.target.contains(p)) open(); });
        document.addEventListener('shopify:section:deselect', function (e) { if (e.target.contains(p)) close(); });
        return;
      }
      if (!enabled) return;
      if (/\/(cart|checkout|account|challenge)/.test(location.pathname)) return;
      var seen = 0;
      try { seen = parseInt(localStorage.getItem(key) || '0', 10); } catch (e) {}
      if (seen && Date.now() - seen < days * 86400000) return;
      // a submitted form comes back with a ?customer_posted=true — never re-open on it
      if (/customer_posted=true/.test(location.search)) { try { localStorage.setItem(key, String(Date.now())); } catch (e) {} return; }
      timer = setTimeout(open, delay * 1000);
      p.addEventListener('submit', function () { try { localStorage.setItem(key, String(Date.now())); } catch (e) {} });
      window.addEventListener('beforeunload', function () { clearTimeout(timer); });
    });
  }

  function init(root) {
    words(root); reveal(root); steps(root); scroller(root); popup(root);
    sweep();
  }
  function boot() {
    init(document);
    window.addEventListener('scroll', sweep, { passive: true });
    window.addEventListener('resize', sweep, { passive: true });
    if ('requestIdleCallback' in window) {
      requestIdleCallback(function () {
        each(document, '.m-product-card__hover-image img', function (i) { i.loading = 'eager'; });
      });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
